import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedProject } from '../../redux/projects/actions';
import { setAlert } from '../../redux/dashboard/actions';
import { getUser } from '../../redux/user/actions';
import { getSelectedSummaryDashboard } from '../../redux/templates/actions';
import { getSelectedCompany } from '../../redux/company/actions';

// Components
import components from '../../components';

// Utilities
import utils from '../../utils';
import calendar from '../../utils/calendar';

import './styles.scss';

const { DateTimeFilterBar, Widget, Loading } = components;

const SummaryContainer = ({ lastUpdated }) => {
  const [sites, setSites] = useState([]); // All sites
  const [siteIds, setSiteIds] = useState([]); // All site IDs
  const [appliedFilters, setAppliedFilters] = useDashboardFilters(null);
  const [currentWidgets, setCurrentWidgets] = useState({});
  const [dashboardData, setDashboardData] = useState([]);
  const [devices, setDevices] = useState([]);

  // Flags
  const [hasTable, setHasTable] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  // Redux variables
  const user = useSelector(getUser);
  const project = useSelector(getSelectedProject);
  const selectedDashboard = useSelector(getSelectedSummaryDashboard);
  const company = useSelector(getSelectedCompany);

  const dispatch = useDispatch();

  const eventHandlers = useRef([]);
  const oldProjectRef = useRef();
  const oldDashboardRef = useRef();
  const queryFilterLoadRef = useRef({ key: '', promise: null });

  // Update applied filters when project changes
  useEffect(() => {
    const fetchDevices = async () => {
      setDevices([]);
      setSites([]);
      await getQueryFilter();
    };

    if (project && user && project.id !== oldProjectRef.current) {
      setLoadingDashboard(true);
      const startDate = !oldProjectRef.current ? calendar.getDefaultDate('start') : appliedFilters.startDate;
      const endDate = !oldProjectRef.current ? calendar.getDefaultDate('end') : appliedFilters.endDate;

      // Load Filters
      setAppliedFilters({
        startDate,
        endDate,
        timezone: project.preferences.timezone,
        interval: calendar.getIntervalById('day'),
        filterByTime: {},
        filterByWeekday: [],
        user,
        selectedDevices: [],
        project,
        projectId: project.id,
        selectedSolution: selectedDashboard,
        selectedSites: [],
        // mobile data extra params
        sites: [],
        startTime: startDate, // Naming convention is different on bigQuery
        endTime: endDate // Naming convention is different on bigQuery
      });
      fetchDevices();

      oldProjectRef.current = project.id;
    }

    return () => {
      setLoadingDashboard(false); // case there is nothing to show, remove loading, need erro message
    };
  }, [project]);

  // Update widgets when applied filters changes
  useEffect(() => {
    const fetchDevices = async () => {
      if (selectedDashboard && selectedDashboard.id !== oldDashboardRef.current) {
        setDevices([]);
        setSites([]);
        await getQueryFilter();
        oldDashboardRef.current = selectedDashboard.id;
      }
      setLoadingDashboard(false);
    };

    if (selectedDashboard && selectedDashboard.id !== oldDashboardRef.current) {
      setLoadingDashboard(true);

      // Load Widgets
      const dashboard = selectedDashboard;
      let newDashboard = {};

      setDashboardData(dashboard);

      // Fetch each widget from cloudStorage
      dashboard.rows.forEach(dashboardRow => {
        // TEST: Replace previous line by next line to test mobile data
        dashboardRow.widgets.map(widgetData => {
          API.getWidget(widgetData.url)
            .then(res => {
              newDashboard = { ...newDashboard, [res.id]: res };
              setCurrentWidgets(newDashboard);
            })
            .catch(onError)
            .finally(() => {
              setLoadingDashboard(false);
            });
        });
      });

      fetchDevices();
      oldDashboardRef.current = selectedDashboard.id;
    }

    API.refreshTokenWhenNeeded();
  }, [selectedDashboard]);

  useEffect(() => {
    if (siteIds.length > 0) {
      setAppliedFilters(prevState => {
        return {
          ...prevState,
          sites: siteIds
        };
      });
    }
  }, [siteIds]);

  useEffect(() => {
    if (devices.length > 0) {
      setAppliedFilters(prevState => {
        return {
          ...prevState,
          selectedDevices: devices
        };
      });
    }
  }, [devices]);

  useEffect(() => {
    if (sites.length > 0) {
      setAppliedFilters(prevState => {
        return {
          ...prevState,
          selectedSites: sites
        };
      });
    }
  }, [sites]);

  // Remove listeners on component did unmount
  useEffect(() => {
    return () => {
      eventHandlers.current.forEach(handler => {
        window.removeEventListener('requestResult', handler);
        window.removeEventListener('requestError', handler);
      });
    };
  }, []);

  const getQueryFilter = async () => {
    if (!project || !selectedDashboard || !selectedDashboard.compatibleDatasources) return;

    const loadKey = `${project.id}:${selectedDashboard.id}`;
    const previousLoad = queryFilterLoadRef.current;
    if (previousLoad.key === loadKey) {
      return previousLoad.promise;
    }

    const loadPromise = loadAllSites();
    queryFilterLoadRef.current = { key: loadKey, promise: loadPromise };

    try {
      return await loadPromise;
    } finally {
      if (queryFilterLoadRef.current.promise === loadPromise) {
        queryFilterLoadRef.current = { key: loadKey, promise: null };
      }
    }
  };

  const loadAllSites = async () => {
    setLoadingDashboard(true);

    if (!company || !project || !selectedDashboard) {
      setLoadingDashboard(false);
      return;
    }

    try {
      const response = await API.getAllSitesWithAssets(project.id);
      const compatibleDatasources = new Set(selectedDashboard.compatibleDatasources || []);
      const sites = (response.sites || []).filter(site =>
        (site.datasources || []).some(datasource => compatibleDatasources.has(datasource))
      );
      const devicesById = new Map();

      sites.forEach(site => {
        (site.assets || []).forEach(asset => {
          (asset.devices || []).forEach(device => devicesById.set(device.id, device));
        });
      });

      setSites(sites);
      setDevices(Array.from(devicesById.values()));
      setSiteIds(sites.map(site => site.id));
    } catch (error) {
      console.error('Error loading Sites with Assets:', error);
      setSites([]);
      setDevices([]);
      setSiteIds([]);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const onFilterSelect = payload => {
    const newAppliedFilters = utils.deepClone(appliedFilters);
    Object.keys(payload).forEach(key => {
      newAppliedFilters[key] = payload[key];
      // Update mobile data params
      if (key === 'startDate') {
        newAppliedFilters['startTime'] = payload[key];
      }
      if (key === 'endDate') {
        newAppliedFilters['endTime'] = payload[key];
      }
    });
    setAppliedFilters(newAppliedFilters);
  };

  const onError = e => {
    console.error(e);
    const error = e?.detail && e?.detail?.message ? e?.detail?.message : 'No details';
    let errorMessage = error;
    if (error && error.includes('Not found: Table')) {
      setHasTable(false);
      errorMessage = 'Table not found';
    }

    dispatch(setAlert(utils.generateAlert(errorMessage ? errorMessage : 'Something went wrong.', 'error')));
  };

  const renderEmptyMessage = () => {
    return (
      <div className="no-data-message flex-column">
        <h1>No data found!</h1>
        <p>Nothing to see here. Try choosing another Project or Solution.</p>
      </div>
    );
  };

  const renderWidgets = () => {
    const isMobileDashboard = selectedDashboard?.headers === 'mobile';
    const hasDevices = devices?.length > 0;
    const hasSites = sites?.length > 0;
    const shouldRenderWidgets = (hasDevices && !isMobileDashboard) || (hasSites && isMobileDashboard);

    if (!shouldRenderWidgets) {
      return renderEmptyMessage();
    }

    if (Object.keys(currentWidgets).length === 0) {
      return null;
    }

    if (dashboardData?.id === 'summary_mobile' && appliedFilters?.sites?.length === 0) {
      return null;
    }

    return dashboardData.rows.map((dashboardRow, index) => (
      <div key={`row-${index}}`} className="chart-row">
        {dashboardRow.widgets.map(widgetData => {
          return (
            <Widget
              key={`analytics-${widgetData.id}`}
              widget={currentWidgets[widgetData.id]}
              lastUpdated={lastUpdated}
              filters={appliedFilters}
              handleError={onError}
              eventHandlers={eventHandlers.current}
            />
          );
        })}
      </div>
    ));
  };

  const renderTopFilterBar = () => {
    if (!appliedFilters) {
      return null;
    }

    return (
      <div className="filter-bar">
        <DateTimeFilterBar
          appliedFilters={{
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            interval: appliedFilters.interval
          }}
          onPeriodSelected={onFilterSelect}
        />
      </div>
    );
  };

  // Prevent rendering when there is no project or when token expires
  // This can happen after logout redirect
  if (!project) {
    return (
      <div className="no-data-message flex-column">
        <h1>No data found!</h1>
        <p>Nothing to see here. Try choosing another site group or creating a new one.</p>
      </div>
    );
  }

  const empty = !hasTable;

  return (
    <div id="summary-container" className="h-100">
      {empty && renderEmptyMessage()}
      {!empty && (
        <>
          {renderTopFilterBar()}
          <div className="scrollable-container">
            <div id="summary-chart-container">
              {loadingDashboard ? (
                <div className="h-100">
                  <Loading size={8} />
                </div>
              ) : (
                renderWidgets()
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SummaryContainer;
